import { format, getDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useBookMakeupMutation } from '~entities/makeup/api/book-makeup';
import type { MakeupBooking } from '~entities/makeup/api/book-makeup';
import { useGetMakeupBalances } from '~entities/makeup/api/get-makeup-balances';
import { useGetMakeupOccurrenceCapacity } from '~entities/makeup/api/get-makeup-occurrence-capacity';
import { useGetMakeupsByStudent } from '~entities/makeup/api/get-makeups-by-student';
import { useGetMakeupTargets } from '~entities/makeup/api/get-makeup-targets';
import type { Student } from '~entities/student';
import { parseTimeToMinutes as parseSharedTimeToMinutes } from '~features/room-overview/lib/timeline';

import type { Days } from '~shared/constants/days';
import { parseToNoonDate } from '~shared/helpers/datetime';
import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';
import { Textarea } from '~shared/shadcn/ui/textarea';
import {
  getErrorMessage,
  isMakeupCapacityFullError,
} from '~shared/utils/error-handler';

const DAYS_OF_WEEK = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
} as const;

type DayNumbers = (typeof DAYS_OF_WEEK)[keyof typeof DAYS_OF_WEEK];

const ACTIVE_MAKEUP_STATUSES = new Set(['requested', 'scheduled']);

function parseTimeToMinutes(time?: string): number | null {
  if (!time?.trim()) return null;
  // Shared helper uses local clock for ISO/HH:mm (matches class schedule display).
  return parseSharedTimeToMinutes(time.trim());
}

function timesOverlap(
  startA?: string,
  endA?: string,
  startB?: string,
  endB?: string,
): boolean {
  const aStart = parseTimeToMinutes(startA);
  const aEnd = parseTimeToMinutes(endA);
  const bStart = parseTimeToMinutes(startB);
  const bEnd = parseTimeToMinutes(endB);
  // Missing times: cannot prove overlap — match BE (allow; do not over-block).
  if (aStart == null || aEnd == null || bStart == null || bEnd == null) {
    return false;
  }
  return aStart < bEnd && bStart < aEnd;
}

function getEnrollmentId(
  value: MakeupBooking['targetEnrollmentId'] | MakeupBooking['sourceEnrollmentId'],
): string {
  if (!value) return '';
  return typeof value === 'string' ? value : value._id;
}

function getEnrollmentTimes(
  value: MakeupBooking['targetEnrollmentId'],
): { startTime?: string; endTime?: string } {
  if (!value || typeof value === 'string') return {};
  return { startTime: value.startTime, endTime: value.endTime };
}

function createMakeupDisabledDates({
  dayNames,
  startDate,
  endDate,
  isDateConflicted,
}: {
  dayNames: Array<Days>;
  startDate: string | Date | null | undefined;
  endDate: string | Date | null | undefined;
  isDateConflicted?: (day: Date) => boolean;
}): (day: Date) => boolean {
  const allowedDays = new Set(dayNames.map((day) => DAYS_OF_WEEK[day]));
  // Class dates arrive as UTC midnight; read the calendar day only so negative
  // offsets don't shift the selectable range by a day.
  const parsedStart = parseToNoonDate(startDate);
  const parsedEnd = parseToNoonDate(endDate);

  if (!parsedStart || !parsedEnd) {
    return () => true;
  }

  const start = startOfDay(parsedStart);
  const end = startOfDay(parsedEnd);

  return (day: Date): boolean => {
    const normalizedDay = startOfDay(day);
    return (
      !allowedDays.has(getDay(day) as DayNumbers) ||
      isBefore(normalizedDay, start) ||
      isAfter(normalizedDay, end) ||
      (isDateConflicted?.(normalizedDay) ?? false)
    );
  };
}

interface ScheduleMakeupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  student: Student | null;
  sourceEnrollmentId: string;
  sourceAttendanceDate: string;
  sessionId: string;
  sendConfirmationDefault?: boolean;
}

export function ScheduleMakeupSheet({
  open,
  onOpenChange,
  student,
  sourceEnrollmentId,
  sourceAttendanceDate,
  sessionId,
  sendConfirmationDefault = true,
}: ScheduleMakeupSheetProps) {
  const [targetEnrollmentId, setTargetEnrollmentId] = useState('');
  const [targetDate, setTargetDate] = useState<Date | undefined>();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [notes, setNotes] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState(
    sendConfirmationDefault,
  );
  // Keep student/balances stable during the close animation after success.
  const [cachedStudent, setCachedStudent] = useState<Student | null>(student);
  const [suppressBalanceBlock, setSuppressBalanceBlock] = useState(false);
  const [capacityOverrideOpen, setCapacityOverrideOpen] = useState(false);
  const [capacityOverrideMessage, setCapacityOverrideMessage] = useState('');

  // Cache student for close animation; do not reset the form here.
  useEffect(() => {
    if (open && student) {
      setCachedStudent(student);
    }
  }, [open, student]);

  // Reset booking fields when the sheet opens for a student/absence.
  // Do not depend on sendConfirmationDefault — settings resolving mid-open
  // would wipe a class the staff already chose, then preselect would restore
  // the source class silently.
  useEffect(() => {
    if (!open) return;
    setSuppressBalanceBlock(false);
    setTargetEnrollmentId('');
    setTargetDate(undefined);
    setNotes('');
    setShowDatePicker(false);
    setCapacityOverrideOpen(false);
    setCapacityOverrideMessage('');
  }, [open, student?._id, sourceEnrollmentId, sourceAttendanceDate]);

  // Sync email default if settings load after open, without resetting the form.
  useEffect(() => {
    if (!open) return;
    setSendConfirmation(sendConfirmationDefault);
  }, [open, sendConfirmationDefault]);

  const activeStudent = student ?? cachedStudent;

  const {
    data: eligibleClasses = [],
    isLoading: isLoadingClasses,
    isError: isClassesError,
  } = useGetMakeupTargets(
    {
      studentId: activeStudent?._id ?? '',
      sessionId,
      sourceEnrollmentId,
    },
    open && !!activeStudent?._id && !!sessionId && !!sourceEnrollmentId,
  );

  // Admin is already on this class's attendance page — preselect it as the
  // makeup target when eligible, while still allowing any other class.
  useEffect(() => {
    if (!open || !sourceEnrollmentId || eligibleClasses.length === 0) {
      return;
    }
    const sourceIsEligible = eligibleClasses.some(
      (classItem) => classItem._id === sourceEnrollmentId,
    );
    if (!sourceIsEligible) {
      return;
    }
    setTargetEnrollmentId((current) => current || sourceEnrollmentId);
  }, [open, sourceEnrollmentId, eligibleClasses]);
  const {
    data: balances,
    isLoading: isLoadingBalances,
    isError: isBalancesError,
  } = useGetMakeupBalances(
    {
      studentId: activeStudent?._id ?? '',
      sessionId,
    },
    open && !!activeStudent?._id && !!sessionId,
  );
  const { data: studentMakeups = [] } = useGetMakeupsByStudent(
    {
      studentId: activeStudent?._id ?? '',
      sessionId,
    },
    open && !!activeStudent?._id && !!sessionId,
  );
  const bookMutation = useBookMakeupMutation();

  const activeMakeups = useMemo(
    () =>
      studentMakeups.filter((booking) =>
        ACTIVE_MAKEUP_STATUSES.has(booking.status),
      ),
    [studentMakeups],
  );

  const selectedClass = useMemo(
    () => eligibleClasses.find((item) => item._id === targetEnrollmentId),
    [eligibleClasses, targetEnrollmentId],
  );

  const targetDateKey = targetDate ? format(targetDate, 'yyyy-MM-dd') : '';
  const {
    data: occurrenceCapacity,
    isLoading: isLoadingCapacity,
    isError: isCapacityError,
  } = useGetMakeupOccurrenceCapacity(
    {
      enrollmentId: targetEnrollmentId,
      date: targetDateKey,
    },
    open && !!targetEnrollmentId && !!targetDateKey,
  );

  const disabledDates = useMemo(() => {
    if (!selectedClass) return () => true;

    const isDateConflicted = (day: Date) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      return activeMakeups.some((booking) => {
        const bookingDate = String(booking.targetDate).slice(0, 10);
        if (bookingDate !== dateKey) return false;

        // Same class occurrence already booked.
        if (getEnrollmentId(booking.targetEnrollmentId) === selectedClass._id) {
          return true;
        }

        // Overlapping time with another active makeup that day.
        const existingTimes = getEnrollmentTimes(booking.targetEnrollmentId);
        return timesOverlap(
          selectedClass.startTime,
          selectedClass.endTime,
          existingTimes.startTime,
          existingTimes.endTime,
        );
      });
    };

    return createMakeupDisabledDates({
      dayNames: selectedClass.days as Days[],
      startDate: selectedClass.startDate,
      endDate: selectedClass.endDate,
      isDateConflicted,
    });
  }, [selectedClass, activeMakeups]);

  const hasBalanceData = balances != null;
  const balancesReady =
    hasBalanceData && !isLoadingBalances && !isBalancesError;
  const familyRemaining = balances?.family.remaining ?? 0;
  const studentRemaining = balances?.student.remaining ?? 0;
  const familyExhausted =
    open && balancesReady && !suppressBalanceBlock && familyRemaining <= 0;
  const studentExhausted =
    open && balancesReady && !suppressBalanceBlock && studentRemaining <= 0;
  const noBalance = familyExhausted || studentExhausted;
  const noEligibleClasses =
    open && !isLoadingClasses && !isClassesError && eligibleClasses.length === 0;
  const formBlocked = noBalance || !balancesReady || noEligibleClasses;

  const balanceBlockReason = (() => {
    if (!noBalance) return null;
    if (familyExhausted && studentExhausted) {
      return 'Family credits and student allotment are used up. This student cannot be booked for another makeup until balances are restored.';
    }
    if (studentExhausted) {
      return 'Student allotment is used up. This student cannot be booked for another makeup until allotment is restored.';
    }
    return 'Family credits are used up. This student cannot be booked for another makeup until credits are restored.';
  })();

  const canSubmit =
    open &&
    !!activeStudent &&
    !!targetEnrollmentId &&
    !!targetDate &&
    balancesReady &&
    !noBalance &&
    !suppressBalanceBlock &&
    !noEligibleClasses;

  const handleSubmit = async (allowOverCapacity = false) => {
    if (!activeStudent || !targetEnrollmentId || !targetDate) {
      toast.error('Select a target class and date.');
      return;
    }
    if (!balancesReady || noBalance) {
      toast.error(
        isBalancesError
          ? 'Could not load makeup balances. Please try again.'
          : 'Makeup balances are not available for booking.',
      );
      return;
    }

    try {
      await bookMutation.mutateAsync({
        studentId: activeStudent._id,
        sourceEnrollmentId,
        sourceAttendanceDate,
        targetEnrollmentId,
        targetDate: format(targetDate, 'yyyy-MM-dd'),
        notes: notes.trim() || undefined,
        sendConfirmation,
        allowOverCapacity: allowOverCapacity || undefined,
      });
      // Avoid flashing "balances used up" while the sheet closes after a
      // successful book (balances refetch + parent clearing student).
      setSuppressBalanceBlock(true);
      setCapacityOverrideOpen(false);
      toast.success(
        allowOverCapacity
          ? 'Makeup scheduled over class capacity.'
          : 'Makeup scheduled.',
      );
      onOpenChange(false);
    } catch (error) {
      if (!allowOverCapacity && isMakeupCapacityFullError(error)) {
        setCapacityOverrideMessage(
          getErrorMessage(
            error,
            'This class is at capacity for the selected date.',
          ),
        );
        setCapacityOverrideOpen(true);
        return;
      }
      toast.error(getErrorMessage(error, 'Failed to schedule makeup.'));
    }
  };

  const classSelectPlaceholder = (() => {
    if (isLoadingClasses) return 'Loading eligible classes…';
    if (isClassesError) return 'Could not load classes';
    if (eligibleClasses.length === 0) return 'No eligible classes';
    return 'Select class';
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full overflow-y-auto sm:max-w-lg'>
        <SheetHeader>
          <SheetTitle>Schedule makeup</SheetTitle>
          <SheetDescription>
            Book {activeStudent?.name ?? 'student'} into a makeup class for the
            absence on {sourceAttendanceDate}.
          </SheetDescription>
        </SheetHeader>

        <div className='mt-6 space-y-5'>
          <div
            className={cn(
              'rounded-lg border p-4',
              (noBalance || isBalancesError) &&
                'border-destructive/40 bg-destructive/5',
            )}
          >
            <p className='mb-2 text-sm font-medium'>Balances</p>
            {isLoadingBalances ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Loading balances…
              </div>
            ) : isBalancesError || !hasBalanceData ? (
              <p className='text-sm text-destructive'>
                Could not load makeup balances. Close and try again, or refresh
                the page.
              </p>
            ) : (
              <>
                <div className='grid grid-cols-2 gap-3 text-sm'>
                  <div>
                    <p className='text-muted-foreground'>Family credits</p>
                    <p
                      className={cn(
                        'font-medium',
                        familyExhausted && 'text-destructive',
                      )}
                    >
                      {familyRemaining} remaining
                      <span className='text-muted-foreground'>
                        {' '}
                        / {balances.family.limit ?? 0}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className='text-muted-foreground'>Student allotment</p>
                    <p
                      className={cn(
                        'font-medium',
                        studentExhausted && 'text-destructive',
                      )}
                    >
                      {studentRemaining} remaining
                      <span className='text-muted-foreground'>
                        {' '}
                        / {balances.student.limit ?? 0}
                      </span>
                    </p>
                  </div>
                </div>
                {balanceBlockReason ? (
                  <p className='mt-3 text-sm text-destructive'>
                    {balanceBlockReason}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium'>Target class</label>
            <Select
              value={targetEnrollmentId}
              onValueChange={(value) => {
                setTargetEnrollmentId(value);
                setTargetDate(undefined);
              }}
              disabled={
                isLoadingClasses ||
                isClassesError ||
                formBlocked ||
                eligibleClasses.length === 0
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={classSelectPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {eligibleClasses.map((classItem) => (
                  <SelectItem key={classItem._id} value={classItem._id}>
                    {classItem.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isClassesError ? (
              <p className='text-sm text-destructive'>
                Could not load eligible makeup classes. Close and try again.
              </p>
            ) : null}
            {noEligibleClasses ? (
              <p className='text-sm text-muted-foreground'>
                No classes match this absence (same location, group, age, and
                session with makeups enabled).
              </p>
            ) : null}
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium'>Target date</label>
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  disabled={!selectedClass || formBlocked}
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !targetDate && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className='mr-2 size-4' />
                  {targetDate
                    ? format(targetDate, 'PPP')
                    : 'Pick a class date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0' align='start'>
                <Calendar
                  mode='single'
                  selected={targetDate}
                  onSelect={(day) => {
                    setTargetDate(day);
                    setShowDatePicker(false);
                  }}
                  disabled={disabledDates}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {targetDateKey ? (
              <p
                className={cn(
                  'text-sm',
                  occurrenceCapacity?.isFull
                    ? 'text-destructive'
                    : 'text-muted-foreground',
                )}
              >
                {isLoadingCapacity
                  ? 'Checking seats for this date…'
                  : isCapacityError
                    ? 'Could not load seats for this date.'
                    : occurrenceCapacity?.remainingSeats == null
                      ? 'No seat limit set for this class.'
                      : occurrenceCapacity.isFull
                        ? `Full on this date (${occurrenceCapacity.occupancy}/${occurrenceCapacity.maxSize}). Staff can still override.`
                        : `${occurrenceCapacity.remainingSeats} seat${
                            occurrenceCapacity.remainingSeats === 1 ? '' : 's'
                          } left on this date (${occurrenceCapacity.occupancy}/${occurrenceCapacity.maxSize}).`}
              </p>
            ) : null}
            {selectedClass ? (
              <p className='text-xs text-muted-foreground'>
                Dates with an overlapping makeup for this student are
                unavailable.
              </p>
            ) : null}
          </div>

          <div className='space-y-2'>
            <label htmlFor='makeup-notes' className='text-sm font-medium'>
              Notes
            </label>
            <Textarea
              id='makeup-notes'
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder='Optional note for staff'
              rows={3}
              disabled={formBlocked}
            />
          </div>

          <label className='flex items-center gap-2 text-sm'>
            <Checkbox
              checked={sendConfirmation}
              onCheckedChange={(checked) =>
                setSendConfirmation(checked === true)
              }
              disabled={formBlocked}
            />
            Send confirmation email to family
          </label>
        </div>

        <SheetFooter className='mt-8 gap-2 sm:space-x-0'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit(false)}
            disabled={!canSubmit || bookMutation.isPending}
          >
            {bookMutation.isPending && !capacityOverrideOpen ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Scheduling…
              </>
            ) : (
              'Schedule makeup'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>

      <AlertDialog
        open={capacityOverrideOpen}
        onOpenChange={(nextOpen) => {
          if (!bookMutation.isPending) {
            setCapacityOverrideOpen(nextOpen);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Class is at capacity</AlertDialogTitle>
            <AlertDialogDescription>
              {capacityOverrideMessage ||
                'This class is full for the selected date. You can still book this makeup as a staff override.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bookMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={bookMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void handleSubmit(true);
              }}
            >
              {bookMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Booking…
                </>
              ) : (
                'Book anyway'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
